import { useBomSupplierInfoUpdateMutation } from "@/features/dashboard/api/queries/bom-supplier-info-query";
import type { BomSupplierInfo } from "@/features/dashboard/types/bom-supplier-info";
import * as Dialog from "@radix-ui/react-dialog";
import { IconX } from "@tabler/icons-react";
import { useState } from "react";
import { toast } from "sonner";
import { Button } from "../core/buttons/button";
import { BomSupplierInfoForm } from "./bom-supplier-info-form";

interface Props {
    supplierInfo: BomSupplierInfo;
    isOpen: boolean;
    onClose: () => void;
    onSuccess?: () => void;
}

export function EditBomSupplierInfoModal({ supplierInfo, isOpen, onClose, onSuccess }: Props) {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const updateMutation = useBomSupplierInfoUpdateMutation(supplierInfo.id!);

    const handleSubmit = async (values: BomSupplierInfo) => {
        setIsSubmitting(true);
        try {
            await updateMutation.mutateAsync(values);
            toast.success("Supplier info updated successfully!");
            onSuccess?.();
            onClose();
        } catch (error: any) {
            if (error?.response?.status === 422) {
                throw error;
            }
            toast.error("Failed to update supplier info");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Dialog.Root open={isOpen} onOpenChange={onClose}>
            <Dialog.Portal>
                <Dialog.Overlay className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm" />
                <Dialog.Content className="fixed left-[50%] top-[50%] z-50 grid w-full max-w-5xl max-h-[90vh] translate-x-[-50%] translate-y-[-50%] gap-4 border bg-white p-6 shadow-lg duration-200 overflow-y-auto rounded-lg">
                    <div className="flex items-center justify-between">
                        <Dialog.Title className="text-xl font-semibold">Edit Supplier Info</Dialog.Title>
                        <Dialog.Close asChild>
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                <IconX className="h-4 w-4" />
                            </Button>
                        </Dialog.Close>
                    </div>
                    <div className="py-4">
                        <BomSupplierInfoForm supplierInfo={supplierInfo} onSubmit={handleSubmit} isSubmitting={isSubmitting} />
                    </div>
                    <div className="flex justify-end gap-2">
                        <Button type="button" variant="ghost" onClick={onClose} disabled={isSubmitting}>
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            form="bom-supplier-info-form"
                            disabled={isSubmitting}
                            onClick={() => {
                                const form = document.querySelector("#bom-supplier-info-form") as HTMLFormElement;
                                if (form) form.requestSubmit();
                            }}
                        >
                            {isSubmitting ? "Saving..." : "Save Changes"}
                        </Button>
                    </div>
                </Dialog.Content>
            </Dialog.Portal>
        </Dialog.Root>
    );
}
